import { useState, type PropsWithChildren } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../lib/auth';
import { apiGet, getApiError } from '../../lib/api';
import { hasChosenShop, markShopChosen } from '../../lib/shopChoice';
import { Wordmark } from '../../components/Logo';
import { ShopsIcon } from '../../components/NavIcons';
import { cn } from '../../lib/cn';

interface ShopOption {
  id: string;
  code: string;
  name: string;
  type: 'HQ' | 'SHOP' | 'ONLINE';
}

/**
 * Netflix-style "which shop are you working from?" chooser.
 *
 * A staff member assigned to more than one shop is asked, right after signing
 * in, to pick the shop she's at today — that becomes her active shop and the
 * whole app (dashboard, stock, cash, orders) scopes to it. We ask once per
 * browser session (see shopChoice.ts); after she picks, reloads go straight
 * through. Single-shop users never see this.
 */
export function ShopChooserGate({ children }: PropsWithChildren): JSX.Element {
  const { user, setActiveShop } = useAuth();
  const allowedShopIds = user?.allowedShopIds ?? [];
  const needsChoice = allowedShopIds.length > 1;

  const [chosen, setChosen] = useState<boolean>(() =>
    user ? hasChosenShop(user.id) : false,
  );
  const [picking, setPicking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const shopsQuery = useQuery({
    queryKey: ['shops'],
    queryFn: () => apiGet<ShopOption[]>('/shops'),
    enabled: needsChoice && !chosen,
  });

  if (!user || !needsChoice || chosen) return <>{children}</>;

  const shops = (shopsQuery.data ?? []).filter((s) => allowedShopIds.includes(s.id));

  const pick = async (shopId: string): Promise<void> => {
    setPicking(shopId);
    setError(null);
    try {
      await setActiveShop(shopId);
      markShopChosen(user.id);
      setChosen(true);
    } catch (err) {
      setError(getApiError(err).message);
      setPicking(null);
    }
  };

  const firstName = (user.name ?? '').split(' (')[0]?.split(' ')[0] ?? '';

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-paper px-6 py-12">
      <div className="w-full max-w-3xl">
        <div className="flex flex-col items-center text-center mb-10">
          <Wordmark size="lg" />
          <h1 className="kc-serif text-4xl text-ink mt-8">
            Where are you working{firstName ? `, ${firstName}` : ''}?
          </h1>
          <p className="text-sm text-ink-3 mt-2">
            Pick the shop you&apos;re at today. Everything — your sales, stock and till —
            will show for that shop. You can switch any time from the sidebar.
          </p>
        </div>

        {shopsQuery.isLoading ? (
          <div className="text-center text-ink-3 text-sm py-12">Loading your shops…</div>
        ) : shops.length === 0 ? (
          <div className="text-center text-ink-3 text-sm py-12">
            No shops are assigned to your account. Ask an admin to set this up.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {shops.map((s) => {
              const isPicking = picking === s.id;
              const disabled = picking !== null;
              return (
                <button
                  key={s.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => void pick(s.id)}
                  className={cn(
                    'group flex flex-col items-center gap-3 rounded-xl border border-rule bg-surface p-6 text-center transition',
                    'hover:border-accent hover:bg-accent-soft focus:outline-none focus:ring-2 focus:ring-accent/40',
                    disabled && !isPicking && 'opacity-50',
                    isPicking && 'border-accent bg-accent-soft',
                  )}
                >
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-paper-2 text-ink-2 group-hover:bg-white group-hover:text-accent transition">
                    <ShopsIcon size={26} />
                  </span>
                  <span className="text-base font-medium text-ink">{s.name}</span>
                  <span className="text-[11px] uppercase tracking-widest text-ink-3">
                    {s.type === 'ONLINE' ? 'Online' : s.code}
                  </span>
                  {isPicking && <span className="text-xs text-accent">Opening…</span>}
                </button>
              );
            })}
          </div>
        )}

        {error && <p className="mt-6 text-center text-sm text-negative">{error}</p>}
      </div>
    </div>
  );
}
